import { Link } from "react-router-dom";
import { Clock, Users, Layers, Calculator, Check, ArrowUpRight, ShieldCheck, Search, Wrench, Mail } from "lucide-react";
import Seo from "@/components/Seo";

const SCHEDULE_MAILTO =
    "mailto:info@vittaniti.com?subject=Schedule%20a%20discovery%20call&body=Hi%20Vittaniti%20team%2C%0A%0AI%27d%20like%20to%20schedule%20a%2030-minute%20discovery%20call.%20Here%20are%20a%20few%20time%20windows%20that%20work%20for%20me%3A%0A%0A%E2%80%A2%20%0A%E2%80%A2%20%0A%E2%80%A2%20%0A%0AThanks!";

const ONBOARDING_STEPS = [
    { icon: Search, day: "Day 1", title: "Discovery", body: "We define your specific requirements in a 30-minute call." },
    { icon: Users, day: "Day 2", title: "Matching", body: "We identify the right senior-led talent for your needs." },
    { icon: Wrench, day: "Day 3–5", title: "Onboarding", body: "Secure access, integration and project kick-off — zero downtime." },
];

const PLANS = [
    {
        id: "hourly",
        icon: Clock,
        name: "Hourly Model",
        tagline: "Perfect for project-based work or variable workloads.",
        features: [
            "Pay only for hours worked",
            "Flexible scaling up or down",
            "Detailed time tracking",
            "No minimum commitment",
            "Ideal for short-term projects",
        ],
        idealFor: "One-time projects, seasonal support, trial engagements",
        cta: "Get Started",
    },
    {
        id: "monthly",
        icon: Users,
        name: "Monthly Dedicated Resource",
        tagline: "A full-time professional, working exclusively for your business.",
        features: [
            "Full-time dedicated professional",
            "Fixed monthly cost",
            "Consistent availability",
            "Deep business knowledge",
            "Long-term partnership",
        ],
        idealFor: "Ongoing operations, core business functions, stable workloads",
        cta: "Get Started",
        popular: true,
    },
    {
        id: "project",
        icon: Layers,
        name: "Project-Based Pricing",
        tagline: "Fixed price for a clearly defined scope and deliverables.",
        features: [
            "Clear scope and timeline",
            "Fixed cost estimation",
            "Milestone-based payments",
            "Defined deliverables",
            "Predictable budgeting",
        ],
        idealFor: "Specific initiatives, system implementations, one-off tasks",
        cta: "Get Started",
    },
];

const INCLUDES = [
    "Dedicated account manager",
    "Regular progress reporting",
    "Quality assurance checks",
    "Secure data handling",
    "4-hour overlap with your business hours",
    "Communication via your preferred channels",
    "Flexible working hours",
    "No setup or hidden fees",
    "Trial period available",
    "Easy scaling options",
];

export default function Pricing() {
    return (
        <div data-testid="pricing-page">
            <Seo
                path="/pricing"
                title="Pricing — Hourly, Monthly Dedicated & Project-Based Engagements"
                description="Transparent engagement models for offshore bookkeeping and back-office work. Hourly, monthly dedicated resource, or project-based — all fully-loaded comparisons against US/EU hiring."
                keywords="offshore bookkeeping pricing, dedicated bookkeeper monthly cost, outsourced accounting price USA, remote team pricing India"
            />
            <section className="container-page pt-16 md:pt-24 pb-10">
                <div className="flex items-center gap-3 mb-6">
                    <span className="ornament w-12" />
                    <p className="overline">Pricing</p>
                    <span aria-hidden="true" className="eyebrow-dot" />
                </div>
                <h1 className="display text-5xl md:text-6xl lg:text-7xl text-brand-inkSoft max-w-4xl">
                    Flexible <span className="italic text-brand-amber">engagement</span> models<span className="brand-dot">.</span>
                </h1>
                <p className="mt-6 max-w-2xl text-brand-inkSoft/70 leading-relaxed">
                    Choose the model that best fits your workload and budget. Every plan
                    includes a dedicated account manager, secure data handling and no hidden fees.
                </p>
            </section>

            {/* 3 plan cards */}
            <section className="container-page pb-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {PLANS.map((p, i) => {
                        const Icon = p.icon;
                        const isPopular = p.popular;
                        return (
                            <article
                                key={p.id}
                                data-testid={`pricing-plan-${p.id}`}
                                className={`card-3d p-8 flex flex-col relative animate-fade-up ${
                                    isPopular
                                        ? "ring-2 ring-brand-amber shadow-2xl md:-translate-y-3 md:scale-[1.02]"
                                        : ""
                                }`}
                                style={{
                                    animationDelay: `${i * 80}ms`,
                                    ...(isPopular
                                        ? {
                                              backgroundColor: "#FFFBF1",
                                              boxShadow: "0 25px 60px -25px rgba(217,138,44,0.45), 0 0 0 1px rgba(217,138,44,0.15)",
                                          }
                                        : {}),
                                }}
                            >
                                {isPopular && (
                                    <span className="absolute -top-3 left-8 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] bg-brand-amber text-white shadow"
                                          data-testid="pricing-popular-badge">
                                        Most popular
                                    </span>
                                )}
                                <span className="grid place-items-center w-12 h-12 rounded-xl bg-brand-creamLight text-brand-ink mb-6">
                                    <Icon size={22} strokeWidth={1.6} />
                                </span>
                                <h3 className="font-serif text-2xl text-brand-inkSoft leading-tight">{p.name}</h3>
                                <p className="mt-2 text-sm text-brand-muted leading-relaxed">{p.tagline}</p>

                                <ul className="mt-6 space-y-2.5 text-sm text-brand-inkSoft/90">
                                    {p.features.map(f => (
                                        <li key={f} className="flex gap-2.5">
                                            <Check size={16} className="text-brand-amber mt-0.5 shrink-0" />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-6 p-4 rounded-xl bg-brand-creamLight border border-brand-border">
                                    <p className="overline mb-1">Ideal for</p>
                                    <p className="text-sm text-brand-inkSoft">{p.idealFor}</p>
                                </div>

                                <Link
                                    to="/contact"
                                    state={{ plan: p.name }}
                                    className={`mt-7 ${isPopular ? "btn-primary" : "btn-ghost"} w-full`}
                                    data-testid={`pricing-cta-${p.id}`}
                                >
                                    {p.cta} <ArrowUpRight size={16} />
                                </Link>
                            </article>
                        );
                    })}
                </div>
            </section>

            {/* SOW guarantee — sits right under the 3 plans */}
            <section className="container-page" data-testid="pricing-sow-callout">
                <div
                    className="rounded-2xl border-2 p-6 md:p-7 flex items-start gap-4"
                    style={{ borderColor: "#F2D9A1", backgroundColor: "#FFF8E8" }}
                >
                    <span className="grid place-items-center w-10 h-10 rounded-xl bg-brand-ink text-brand-amber shrink-0">
                        <ShieldCheck size={18} strokeWidth={1.6} />
                    </span>
                    <p className="text-sm text-brand-ink/85 leading-relaxed">
                        <span className="font-semibold text-brand-ink">No surprises, ever.</span>{" "}
                        Project-Based pricing includes a clearly defined Scope of Work (SOW)
                        agreement. Any changes outside the initial SOW are billed at pre-agreed
                        rates — ensuring zero billing surprises.
                    </p>
                </div>
            </section>

            {/* Onboarding timeline — Time to value */}
            <section className="section container-page" data-testid="pricing-onboarding">
                <div className="max-w-3xl mb-10">
                    <p className="overline mb-3">Time to value</p>
                    <h2 className="display text-4xl md:text-5xl text-brand-inkSoft leading-tight">
                        From discovery to dedicated work — <span className="italic text-brand-amber">in five days</span><span className="brand-dot">.</span>
                    </h2>
                    <p className="mt-5 text-brand-ink/70 leading-relaxed">
                        Our service feels like a fast, professional plug-in — not a long,
                        drawn-out recruitment process.
                    </p>
                </div>
                <ol className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {ONBOARDING_STEPS.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <li
                                key={s.day}
                                className="card-3d p-7 flex flex-col gap-5 relative animate-fade-up"
                                style={{ animationDelay: `${i * 90}ms` }}
                                data-testid={`pricing-onb-${i}`}
                            >
                                <span className="absolute top-6 right-6 font-serif text-5xl text-brand-creamLight leading-none select-none">
                                    0{i + 1}
                                </span>
                                <span className="grid place-items-center w-12 h-12 rounded-xl bg-brand-creamLight text-brand-ink">
                                    <Icon size={22} strokeWidth={1.6} />
                                </span>
                                <div>
                                    <p className="overline text-brand-amber">{s.day}</p>
                                    <h3 className="font-serif text-2xl text-brand-ink mt-1 leading-tight">{s.title}</h3>
                                    <p className="text-sm text-brand-ink/70 leading-relaxed mt-3">{s.body}</p>
                                </div>
                            </li>
                        );
                    })}
                </ol>
            </section>

            {/* Build your team */}
            <section className="section container-page">
                <div className="card-3d p-10 md:p-14 grid md:grid-cols-12 gap-10 items-center text-white"
                     style={{ backgroundColor: "#0E4F47", borderColor: "rgba(255,255,255,0.06)" }}
                     data-testid="build-team-card">
                    <div className="md:col-span-7">
                        <p className="overline text-brand-amber">Build your remote team</p>
                        <h2 className="display text-4xl md:text-5xl text-white mt-3 leading-tight">
                            Need multiple people or a custom mix?
                        </h2>
                        <p className="mt-5 text-white/75 leading-relaxed max-w-lg">
                            We&apos;ll assemble a tailored team — multiple roles, a custom service mix,
                            and pricing that flexes with your stage.
                        </p>
                        <Link to="/contact" className="btn-accent mt-8" data-testid="build-team-cta">
                            Build your team <ArrowUpRight size={16} />
                        </Link>
                    </div>
                    <div className="md:col-span-5 grid grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
                        {[
                            { v: "2–5", l: "Team members" },
                            { v: "Custom", l: "Service mix" },
                            { v: "Flexible", l: "Pricing" },
                        ].map(b => (
                            <div key={b.l} className="p-6 text-center" style={{ backgroundColor: "#0E4F47" }}>
                                <p className="font-serif text-3xl text-white">{b.v}</p>
                                <p className="overline text-white/55 mt-2">{b.l}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* What's included */}
            <section className="section bg-brand-creamLight border-y border-brand-border">
                <div className="container-page">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                        <div className="max-w-2xl">
                            <p className="overline mb-3">Included with every plan</p>
                            <h2 className="display text-4xl md:text-5xl text-brand-inkSoft">
                                The Vittaniti baseline.
                            </h2>
                        </div>
                        <p className="text-sm text-brand-muted md:max-w-xs">
                            No matter which model you pick, these are non-negotiables for us.
                        </p>
                    </div>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {INCLUDES.map((item, i) => (
                            <li
                                key={item}
                                className="card-3d p-5 flex items-center gap-3 animate-fade-up"
                                style={{ animationDelay: `${i * 40}ms` }}
                                data-testid={`included-${i}`}
                            >
                                <span className="grid place-items-center w-8 h-8 rounded-full bg-brand-ink/10 text-brand-ink shrink-0">
                                    <Check size={16} />
                                </span>
                                <span className="text-sm text-brand-inkSoft/90">{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Cost savings calculator */}
            <section className="section container-page">
                <div className="grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-3 mb-5">
                            <span className="grid place-items-center w-10 h-10 rounded-xl bg-brand-creamLight text-brand-ink">
                                <Calculator size={18} strokeWidth={1.6} />
                            </span>
                            <p className="overline">Cost savings</p>
                        </div>
                        <h2 className="display text-4xl md:text-5xl text-brand-inkSoft leading-tight">
                            On average, clients save <span className="italic text-brand-amber">50–60%</span> on
                            operational costs compared to local European hiring.
                        </h2>
                        <p className="mt-6 text-brand-inkSoft/70 leading-relaxed">
                            We benchmark against fully-loaded annual costs in the EU — salary, benefits,
                            taxes and overhead — so you see the real apples-to-apples picture.
                        </p>
                        <div
                            className="mt-6 p-4 rounded-xl border-l-4"
                            style={{ borderLeftColor: "#E53935", backgroundColor: "#FFF8E8" }}
                            data-testid="pricing-fully-loaded-note"
                        >
                            <p className="text-sm text-brand-ink/85 leading-relaxed">
                                <span className="font-semibold">Apples-to-apples:</span> these
                                estimates include fully-loaded costs (salary, local payroll taxes,
                                office space, hardware and benefits) — a true 1:1 comparison against
                                local hiring.
                            </p>
                        </div>
                    </div>
                    <div className="lg:col-span-7 grid md:grid-cols-2 gap-6" data-testid="calculator-grid">
                        <article className="card-3d p-8">
                            <p className="overline text-brand-muted">Local hiring (Europe)</p>
                            <p className="font-serif text-5xl md:text-6xl text-brand-inkSoft mt-3">€40,000+</p>
                            <p className="text-sm text-brand-muted mt-4 leading-relaxed">
                                Annual cost per employee including salary, benefits and overhead.
                            </p>
                        </article>
                        <article className="card-3d p-8 text-white relative overflow-hidden"
                                 style={{ backgroundColor: "#0E4F47", borderColor: "rgba(255,255,255,0.06)" }}>
                            <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] bg-brand-amber text-white">
                                With Vittaniti
                            </span>
                            <p className="overline text-white/60">Vittaniti dedicated resource</p>
                            <p className="font-serif text-5xl md:text-6xl text-white mt-3">€16,000+</p>
                            <p className="text-sm text-white/70 mt-4 leading-relaxed">
                                Annual cost for a dedicated, fully-supported resource with account management.
                            </p>
                        </article>
                    </div>
                </div>
            </section>

            {/* Data security & NDA assurance */}
            <section className="container-page pb-8" data-testid="pricing-security">
                <div
                    className="card-3d p-8 md:p-10 grid md:grid-cols-12 gap-6 items-center"
                    style={{ backgroundColor: "#0F1B2E", borderColor: "rgba(255,255,255,0.06)" }}
                >
                    <div className="md:col-span-2 grid place-items-center">
                        <span className="grid place-items-center w-16 h-16 rounded-2xl bg-brand-amber/20 text-brand-amber">
                            <ShieldCheck size={32} strokeWidth={1.5} />
                        </span>
                    </div>
                    <div className="md:col-span-10">
                        <p className="overline text-brand-amber mb-2">Your data, protected</p>
                        <p className="text-white text-lg leading-relaxed">
                            We operate with industry-standard encryption, secure VPN access and
                            strict data-privacy protocols.{" "}
                            <span className="text-brand-amber font-semibold">
                                We&apos;re happy to sign an NDA before our discovery call.
                            </span>
                        </p>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="container-page pb-24">
                <div className="card-3d p-10 md:p-14 text-center max-w-3xl mx-auto">
                    <p className="overline mb-3">Ready to talk numbers?</p>
                    <h2 className="display text-4xl md:text-5xl text-brand-inkSoft">
                        Every business is unique.
                    </h2>
                    <p className="mt-4 text-brand-inkSoft/70 max-w-xl mx-auto leading-relaxed">
                        Tell us your scope and we&apos;ll come back with a custom pricing proposal — usually within one business day.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                        <Link to="/contact" className="btn-primary" data-testid="pricing-final-quote">
                            Get a custom quote <ArrowUpRight size={16} />
                        </Link>
                        <a href={SCHEDULE_MAILTO} className="btn-ghost" data-testid="pricing-final-schedule">
                            <Mail size={15} /> Schedule a call
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
